/**
 * Browser analytics client
 * PURPOSE: Creates anonymous IDs and sends page/property/time events to the backend.
 *
 * EDITING GUIDE:
 * - Read the comments above each section or function before changing behavior.
 * - Keep validation, authentication and error handling in place for production safety.
 * - Search for "EDIT HERE" to find the most common business-content settings.
 */

import { api, API_BASE_URL } from './api';

/**
 * Create a non-sensitive anonymous identifier for analytics storage.
 */
function randomId(prefix) {
  if (window.crypto?.randomUUID) return `${prefix}_${window.crypto.randomUUID()}`;
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export function getVisitorId() {
  const key = 'capital-visitor-id';
  let value = localStorage.getItem(key);
  if (!value) {
    value = randomId('visitor');
    localStorage.setItem(key, value);
  }
  return value;
}

export function getSessionId() {
  const key = 'capital-session-id';
  let value = sessionStorage.getItem(key);
  if (!value) {
    value = randomId('session');
    sessionStorage.setItem(key, value);
  }
  return value;
}

/**
 * Build the common analytics payload shared by normal and beacon requests.
 */
function eventPayload(eventType, extra = {}) {
  return {
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
    eventType,
    path: window.location.pathname,
    referrer: document.referrer,
    ...extra,
  };
}

export async function trackEvent(eventType, extra = {}) {
  try {
    await api.post('/api/analytics/event', eventPayload(eventType, extra));
  } catch {
    // Analytics must never block or break the visitor experience.
  }
}

export function trackEventBeacon(eventType, extra = {}) {
  const payload = eventPayload(eventType, extra);
  const endpoint = `${API_BASE_URL}/api/analytics/event`;

  try {
    if (navigator.sendBeacon) {
      const body = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      if (navigator.sendBeacon(endpoint, body)) return;
    }
  } catch {
    // Fall through to the regular request below.
  }

  trackEvent(eventType, extra);
}
